import {
    Box, Chip, CircularProgress, FormControl, IconButton,
    InputLabel, MenuItem, Paper, Rating, Select, Stack, Tab, Table,
    TableBody, TableCell, TableContainer, TableHead, TablePagination,
    TableRow, Tabs, TextField, Tooltip, Typography
} from '@mui/material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RestoreIcon from '@mui/icons-material/Restore';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

const statusText = {
    published: 'Publicada',
    hidden: 'Oculta',
    deleted_by_customer: 'Eliminada por cliente'
};

const dateText = value => value ?
    new Intl.DateTimeFormat('es-MX', {
        dateStyle: 'medium',
        timeStyle: 'short',
        timeZone: 'America/Mexico_City'
    }).format(new Date(value))
:
    'Fecha desconocida';

const ReviewTable = ({
    busyId,
    error,
    filters,
    loading,
    onDetail,
    onFilterChange,
    onModerate,
    onPageChange,
    onReply,
    onRowsPerPageChange,
    onWithdraw,
    pagination,
    reviews
}) => (
    <Box>
        <Typography sx={{ mb: 2 }} variant="h5">Gestión de reseñas</Typography>

        <Paper variant="outlined">
            <Tabs
                onChange={(event, value) => onFilterChange('status', value)}
                value={filters.status}
                variant="scrollable"
            >
                <Tab label="Publicadas" value="published" />
                <Tab label="Ocultas" value="hidden" />
                <Tab label="Eliminadas por cliente" value="deleted_by_customer" />
                <Tab label="Todas" value="all" />
            </Tabs>

            <Stack
                direction={{ xs: 'column', lg: 'row' }}
                spacing={2}
                sx={{ p: 2 }}
            >
                <TextField
                    fullWidth
                    label="Buscar producto, cliente o comentario"
                    onChange={({ target }) => onFilterChange('search', target.value)}
                    size="small"
                    value={filters.search}
                />
                <FormControl size="small" sx={{ minWidth: 145 }}>
                    <InputLabel>Calificación</InputLabel>
                    <Select
                        label="Calificación"
                        onChange={({ target }) => onFilterChange('rating', target.value)}
                        value={filters.rating}
                    >
                        <MenuItem value="">Todas</MenuItem>
                        {[5, 4, 3, 2, 1].map(value =>
                            <MenuItem key={value} value={value}>{value} estrellas</MenuItem>
                        )}
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 155 }}>
                    <InputLabel>Compra</InputLabel>
                    <Select
                        label="Compra"
                        onChange={({ target }) => onFilterChange('verified', target.value)}
                        value={filters.verified}
                    >
                        <MenuItem value="">Todas</MenuItem>
                        <MenuItem value="true">Verificada</MenuItem>
                        <MenuItem value="false">No verificada</MenuItem>
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Periodo</InputLabel>
                    <Select
                        label="Periodo"
                        onChange={({ target }) => onFilterChange('range', target.value)}
                        value={filters.range}
                    >
                        <MenuItem value="all">Todo el histórico</MenuItem>
                        <MenuItem value="30d">Últimos 30 días</MenuItem>
                        <MenuItem value="90d">Últimos 90 días</MenuItem>
                        <MenuItem value="365d">Últimos 365 días</MenuItem>
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 145 }}>
                    <InputLabel>Contenido</InputLabel>
                    <Select
                        label="Contenido"
                        onChange={({ target }) => onFilterChange('has_images', target.value)}
                        value={filters.has_images}
                    >
                        <MenuItem value="">Con o sin fotos</MenuItem>
                        <MenuItem value="true">Con fotos</MenuItem>
                        <MenuItem value="false">Sin fotos</MenuItem>
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 155 }}>
                    <InputLabel>Respuesta</InputLabel>
                    <Select
                        label="Respuesta"
                        onChange={({ target }) => onFilterChange('has_reply', target.value)}
                        value={filters.has_reply}
                    >
                        <MenuItem value="">Todas</MenuItem>
                        <MenuItem value="true">Respondidas</MenuItem>
                        <MenuItem value="false">Sin responder</MenuItem>
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Ordenar</InputLabel>
                    <Select
                        label="Ordenar"
                        onChange={({ target }) => onFilterChange('sort', target.value)}
                        value={filters.sort}
                    >
                        <MenuItem value="newest">Más recientes</MenuItem>
                        <MenuItem value="oldest">Más antiguas</MenuItem>
                        <MenuItem value="highest">Mayor calificación</MenuItem>
                        <MenuItem value="lowest">Menor calificación</MenuItem>
                        <MenuItem value="product">Producto</MenuItem>
                    </Select>
                </FormControl>
            </Stack>

            {error &&
                <Typography color="error" sx={{ px: 2, pb: 2 }}>
                    {error}
                </Typography>
            }

            <TableContainer>
                <Table sx={{ minWidth: 1050 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Producto</TableCell>
                            <TableCell>Calificación</TableCell>
                            <TableCell>Comentario</TableCell>
                            <TableCell>Cliente</TableCell>
                            <TableCell>Fecha</TableCell>
                            <TableCell>Estado</TableCell>
                            <TableCell align="right">Acciones</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading &&
                            <TableRow>
                                <TableCell align="center" colSpan={7}>
                                    <CircularProgress size={28} />
                                </TableCell>
                            </TableRow>
                        }
                        {!loading && reviews.map(review => {
                            const isBusy = busyId === review.id;
                            const hasReply = review.admin_reply?.active;

                            return (
                                <TableRow hover key={review.id}>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {review.product.title}
                                        </Typography>
                                        {review.variant?.title &&
                                            <Typography color="text.secondary" variant="caption">
                                                {review.variant.title}
                                            </Typography>
                                        }
                                    </TableCell>
                                    <TableCell>
                                        <Rating readOnly size="small" value={review.rating} />
                                    </TableCell>
                                    <TableCell sx={{ maxWidth: 300 }}>
                                        <Typography noWrap variant="body2">
                                            {review.comment}
                                        </Typography>
                                        <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                                            {review.images.length > 0 &&
                                                <Chip label={`${review.images.length} fotos`} size="small" />
                                            }
                                            {hasReply &&
                                                <Chip color="info" label="Respondida" size="small" />
                                            }
                                            {review.edited_after_reply &&
                                                <Chip color="warning" label="Editada después" size="small" />
                                            }
                                        </Stack>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {review.customer.name}
                                        </Typography>
                                        {review.verified_purchase &&
                                            <Chip
                                                color="success"
                                                label="Compra verificada"
                                                size="small"
                                                variant="outlined"
                                            />
                                        }
                                    </TableCell>
                                    <TableCell>{dateText(review.created_at)}</TableCell>
                                    <TableCell>
                                        <Chip
                                            color={review.status === 'published' ?
                                                'success'
                                            : review.status === 'hidden' ?
                                                'warning'
                                            :
                                                'default'}
                                            label={statusText[review.status] || review.status}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Tooltip title="Ver detalle">
                                            <IconButton onClick={() => onDetail(review)}>
                                                <VisibilityIcon />
                                            </IconButton>
                                        </Tooltip>
                                        {review.active &&
                                            <Tooltip title={hasReply ? 'Editar respuesta' : 'Responder'}>
                                                <IconButton
                                                    disabled={isBusy}
                                                    onClick={() => onReply(review)}
                                                >
                                                    <ChatBubbleOutlineIcon />
                                                </IconButton>
                                            </Tooltip>
                                        }
                                        {hasReply &&
                                            <Tooltip title="Retirar respuesta">
                                                <IconButton
                                                    disabled={isBusy}
                                                    onClick={() => onWithdraw(review)}
                                                >
                                                    <DeleteOutlineIcon />
                                                </IconButton>
                                            </Tooltip>
                                        }
                                        {review.status === 'published' &&
                                            <Tooltip title="Ocultar">
                                                <IconButton
                                                    color="warning"
                                                    disabled={isBusy}
                                                    onClick={() => onModerate(review)}
                                                >
                                                    <VisibilityOffIcon />
                                                </IconButton>
                                            </Tooltip>
                                        }
                                        {review.status === 'hidden' &&
                                            <Tooltip title="Restaurar">
                                                <IconButton
                                                    color="success"
                                                    disabled={isBusy}
                                                    onClick={() => onModerate(review)}
                                                >
                                                    <RestoreIcon />
                                                </IconButton>
                                            </Tooltip>
                                        }
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                        {!loading && reviews.length === 0 &&
                            <TableRow>
                                <TableCell align="center" colSpan={7}>
                                    No hay reseñas para mostrar con estos filtros.
                                </TableCell>
                            </TableRow>
                        }
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div"
                count={pagination.total || 0}
                labelRowsPerPage="Filas por página"
                onPageChange={(event, page) => onPageChange(page + 1)}
                onRowsPerPageChange={({ target }) => onRowsPerPageChange(
                    Number(target.value)
                )}
                page={Math.max((pagination.page || 1) - 1, 0)}
                rowsPerPage={pagination.limit || 25}
                rowsPerPageOptions={[10, 25, 50, 100]}
            />
        </Paper>
    </Box>
);

export default ReviewTable;
